import { useState, useMemo } from 'react';
import { useCurrency } from './useCurrency';

// Types pour les filtres de graphiques
export interface ChartDateRange {
  startDate: string;
  endDate: string;
  period: 'last7days' | 'last30days' | 'last3months' | 'last6months' | 'last12months' | 'thisYear' | 'lastYear' | 'custom';
}

export interface RevenueExpenseData {
  date: string;
  revenue: number;
  expenses: number;
  month?: string;
  year?: number;
}

export interface ExpenseBreakdownData {
  name: string;
  value: number;
  color: string;
  percentage?: number;
}

export interface ChartFilters {
  dateRange: ChartDateRange;
  categories?: string[];
  showRevenue: boolean;
  showExpenses: boolean;
}

// Hook pour la gestion des filtres et données de graphiques
export function useChartsData() {
  const { currentCurrency } = useCurrency();
  
  // État des filtres
  const [filters, setFilters] = useState<ChartFilters>({
    dateRange: {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth() - 11, 1).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      period: 'last12months'
    },
    showRevenue: true,
    showExpenses: true
  });

  const [loading] = useState(false);

  // Génération de données mockées pour le développement
  const generateMockRevenueExpenseData = (dateRange: ChartDateRange): RevenueExpenseData[] => {
    const data: RevenueExpenseData[] = [];
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Générer des données mensuelles
    const currentDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    while (currentDate <= endDate) {
      const monthIndex = currentDate.getMonth();
      const year = currentDate.getFullYear();
      
      // Simulation de données avec variations saisonnières
      const baseRevenue = 15000000 + (Math.sin(monthIndex / 12 * Math.PI * 2) * 3000000);
      const baseExpenses = 8000000 + (Math.sin(monthIndex / 12 * Math.PI * 2) * 2000000);
      
      // Ajout de variabilité
      const revenue = baseRevenue + (Math.random() - 0.5) * 2000000;
      const expenses = baseExpenses + (Math.random() - 0.5) * 1500000;
      
      data.push({
        date: currentDate.toISOString().split('T')[0],
        revenue: Math.max(0, revenue),
        expenses: Math.max(0, expenses),
        month: currentDate.toLocaleDateString('fr-FR', { month: 'short' }),
        year
      });
      
      currentDate.setMonth(currentDate.getMonth() + 1);
    }
    
    return data;
  };

  // Génération de données de répartition des charges
  const generateMockExpenseBreakdown = (): ExpenseBreakdownData[] => {
    const categories = [
      { name: 'Salaires & Charges sociales', base: 4500000, color: '#ef4444' },
      { name: 'Loyers & Charges locatives', base: 1200000, color: '#f97316' },
      { name: 'Achats de marchandises', base: 2800000, color: '#eab308' },
      { name: 'Services externes', base: 800000, color: '#22c55e' },
      { name: 'Frais financiers', base: 300000, color: '#3b82f6' },
      { name: 'Impôts & Taxes', base: 600000, color: '#8b5cf6' },
      { name: 'Autres charges', base: 400000, color: '#6b7280' }
    ];

    return categories.map(category => {
      // Ajouter de la variabilité selon la période
      const variation = (Math.random() - 0.5) * 0.3; // ±30%
      const value = category.base * (1 + variation);
      
      return {
        name: category.name,
        value: Math.max(0, value),
        color: category.color
      };
    });
  };

  // Calcul des totaux et pourcentages pour la répartition
  const processExpenseBreakdown = (data: ExpenseBreakdownData[]): ExpenseBreakdownData[] => {
    const total = data.reduce((sum, item) => sum + item.value, 0);
    
    return data.map(item => ({
      ...item,
      percentage: (item.value / total) * 100
    }));
  };

  // Données mémorisées basées sur les filtres
  const revenueExpenseData = useMemo(() => {
    return generateMockRevenueExpenseData(filters.dateRange);
  }, [filters.dateRange]);

  const expenseBreakdownData = useMemo(() => {
    const rawData = generateMockExpenseBreakdown();
    return processExpenseBreakdown(rawData);
  }, [filters.dateRange]);

  // Fonctions pour mettre à jour les filtres
  const updateDateRange = (newDateRange: Partial<ChartDateRange>) => {
    setFilters(prev => ({
      ...prev,
      dateRange: { ...prev.dateRange, ...newDateRange }
    }));
  };

  const setPeriod = (period: ChartDateRange['period']) => {
    const now = new Date();
    let startDate: string;
    let endDate = now.toISOString().split('T')[0];

    switch (period) {
      case 'last7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'last3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0];
        break;
      case 'last6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1).toISOString().split('T')[0];
        break;
      case 'last12months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 12, 1).toISOString().split('T')[0];
        break;
      case 'thisYear':
        startDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      case 'lastYear':
        startDate = new Date(now.getFullYear() - 1, 0, 1).toISOString().split('T')[0];
        endDate = new Date(now.getFullYear() - 1, 11, 31).toISOString().split('T')[0];
        break;
      default:
        return; // Pour 'custom', ne pas modifier les dates
    }

    setFilters(prev => ({
      ...prev,
      dateRange: {
        ...prev.dateRange,
        startDate,
        endDate,
        period
      }
    }));
  };

  const toggleRevenueVisibility = () => {
    setFilters(prev => ({
      ...prev,
      showRevenue: !prev.showRevenue
    }));
  };

  const toggleExpensesVisibility = () => {
    setFilters(prev => ({
      ...prev,
      showExpenses: !prev.showExpenses
    }));
  };

  // Formatage des montants
  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('fr-CD', {
      style: 'currency',
      currency: currentCurrency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactAmount = (amount: number): string => {
    const currencySymbol = currentCurrency === 'CDF' ? 'FC' : currentCurrency;
    
    let result: string;
    if (amount >= 1000000000) {
      result = `${(amount / 1000000000).toFixed(1)}Md ${currencySymbol}`;
    } else if (amount >= 1000000) {
      result = `${(amount / 1000000).toFixed(1)}M ${currencySymbol}`;
    } else if (amount >= 1000) {
      result = `${(amount / 1000).toFixed(0)}k ${currencySymbol}`;
    } else {
      result = formatAmount(amount);
    }
    return result;
  };

  return {
    // Données
    revenueExpenseData,
    expenseBreakdownData,
    
    // Filtres
    filters,
    updateDateRange,
    setPeriod,
    toggleRevenueVisibility,
    toggleExpensesVisibility,
    
    // Utilitaires
    formatAmount,
    formatCompactAmount,
    loading,
    
    // Métadonnées
    currentCurrency
  };
}
